import React, { useState, useEffect, useRef, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { 
  Trophy, 
  RefreshCcw, 
  Play, 
  Film, 
  Code2, 
  PenTool, 
  Lightbulb,
  Heart,
  Zap,
  Target,
  HelpCircle
} from "lucide-react";
import GameInstructionsModal from "./ui/GameInstructionsModal";

const SKILLS = [
  { id: "film", icon: Film, label: "Film", color: "#e4c77a" },
  { id: "tech", icon: Code2, label: "Tech", color: "#61DAFB" },
  { id: "design", icon: PenTool, label: "Design", color: "#ff0055" },
  { id: "strategy", icon: Lightbulb, label: "Strategy", color: "#00ff88" }
];

const GAME_WIDTH = 800;
const GAME_HEIGHT = 500;
const PLAYER_WIDTH = 120;
const PLAYER_HEIGHT = 20;

const TalentMatcherGame = () => {
  const [gameState, setGameState] = useState("start"); // start, playing, gameOver
  const [score, setScore] = useState(0);
  const [highScore, setHighScore] = useState(parseInt(localStorage.getItem("meraki_talent_high_score") || "0"));
  const [targetSkill, setTargetSkill] = useState(SKILLS[0]);
  const [health, setHealth] = useState(3);
  const [showInstructions, setShowInstructions] = useState(false);
  
  const canvasRef = useRef(null);
  const containerRef = useRef(null);
  const requestRef = useRef(null);
  const playerX = useRef(GAME_WIDTH / 2 - PLAYER_WIDTH / 2);
  const objectsRef = useRef([]);
  const mouseX = useRef(GAME_WIDTH / 2);

  const [isTouchDevice, setIsTouchDevice] = useState(false);

  // Refs for dynamic dimensions
  const canvasWidth = useRef(800);
  const canvasHeight = useRef(500);

  useEffect(() => {
    const checkTouch = () => {
      setIsTouchDevice('ontouchstart' in window || navigator.maxTouchPoints > 0 || window.matchMedia("(pointer: coarse)").matches);
    };

    const updateCanvasSize = () => {
      if (containerRef.current && canvasRef.current) {
        const { clientWidth, clientHeight } = containerRef.current;
        canvasWidth.current = clientWidth * window.devicePixelRatio;
        canvasHeight.current = clientHeight * window.devicePixelRatio;
        canvasRef.current.width = canvasWidth.current;
        canvasRef.current.height = canvasHeight.current;
      }
    };

    // Force non-passive touchmove listener to prevent scrolling
    const container = containerRef.current;
    const preventDefault = (e) => {
      if (container && container.contains(e.target)) {
        e.preventDefault();
      }
    };

    if (container) {
      container.addEventListener('touchmove', preventDefault, { passive: false });
    }

    checkTouch();
    updateCanvasSize();
    window.addEventListener('resize', updateCanvasSize);
    return () => {
      window.removeEventListener('resize', updateCanvasSize);
      if (container) {
        container.removeEventListener('touchmove', preventDefault);
      }
    };
  }, []);

  const startLevel = useCallback(() => {
    setGameState("playing");
    setScore(0);
    setHealth(3);
    objectsRef.current = [];
    setTargetSkill(SKILLS[Math.floor(Math.random() * SKILLS.length)]);
  }, []);

  const update = useCallback(() => {
    // Smooth player follow
    const targetX = mouseX.current - PLAYER_WIDTH / 2;
    playerX.current += (targetX - playerX.current) * 0.2;
    
    // Bounds
    if (playerX.current < 0) playerX.current = 0;
    if (playerX.current > canvasWidth.current - PLAYER_WIDTH) playerX.current = canvasWidth.current - PLAYER_WIDTH;

    // Spawn objects
    if (Math.random() < 0.03 + (score * 0.001)) {
      objectsRef.current.push({
        x: Math.random() * (canvasWidth.current - 40),
        y: -40,
        skill: SKILLS[Math.floor(Math.random() * SKILLS.length)],
        speed: 3 + Math.random() * 2 + (score * 0.1),
        id: Math.random()
      });
    }

    // Move & Collide
    objectsRef.current.forEach((obj, index) => {
      obj.y += obj.speed;

      // Collision check
      if (
        obj.y + 30 > canvasHeight.current - PLAYER_HEIGHT - 20 &&
        obj.y < canvasHeight.current - 20 &&
        obj.x + 30 > playerX.current &&
        obj.x < playerX.current + PLAYER_WIDTH
      ) {
        if (obj.skill.id === targetSkill.id) {
          setScore(s => {
            const newScore = s + 10;
            // Change target skill every 50 points
            if (newScore % 50 === 0) {
              setTargetSkill(SKILLS[Math.floor(Math.random() * SKILLS.length)]);
            }
            return newScore;
          });
        } else {
          setHealth(h => {
            if (h <= 1) setGameState("gameOver");
            return h - 1;
          });
        }
        objectsRef.current.splice(index, 1);
      }

      // Out of bounds
      if (obj.y > canvasHeight.current) {
        if (obj.skill.id === targetSkill.id) {
           // Missed a target skill
           setHealth(h => {
            if (h <= 1) setGameState("gameOver");
            return h - 1;
          });
        }
        objectsRef.current.splice(index, 1);
      }
    });
  }, [score, targetSkill]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");

    const draw = () => {
      ctx.clearRect(0, 0, canvasWidth.current, canvasHeight.current);

      // Background Grid
      ctx.strokeStyle = "rgba(201, 169, 97, 0.05)";
      ctx.lineWidth = 1;
      for (let x = 0; x < canvasWidth.current; x += 50) {
        ctx.beginPath(); ctx.moveTo(x, 0); ctx.lineTo(x, canvasHeight.current); ctx.stroke();
      }
      for (let y = 0; y < canvasHeight.current; y += 50) {
        ctx.beginPath(); ctx.moveTo(0, y); ctx.lineTo(canvasWidth.current, y); ctx.stroke();
      }

      // Draw Player Scanner
      const pX = playerX.current;
      const pY = canvasHeight.current - 40;
      
      // Glow
      ctx.shadowBlur = 15;
      ctx.shadowColor = targetSkill.color;
      
      const grad = ctx.createLinearGradient(pX, pY, pX + PLAYER_WIDTH, pY);
      grad.addColorStop(0, "transparent");
      grad.addColorStop(0.5, targetSkill.color);
      grad.addColorStop(1, "transparent");
      
      ctx.fillStyle = grad;
      ctx.fillRect(pX, pY, PLAYER_WIDTH, 4);
      
      // Scanner detail
      ctx.fillStyle = "#fff";
      ctx.fillRect(pX + PLAYER_WIDTH / 2 - 1, pY - 2, 2, 8);

      // Draw Objects
      objectsRef.current.forEach(obj => {
        ctx.shadowBlur = 10;
        ctx.shadowColor = obj.skill.color;
        ctx.fillStyle = obj.skill.color;
        
        // Draw icon-like shape
        ctx.beginPath();
        ctx.arc(obj.x + 15, obj.y + 15, 12, 0, Math.PI * 2);
        ctx.fill();
        
        // Inner detail
        ctx.fillStyle = "#fff";
        ctx.beginPath();
        ctx.arc(obj.x + 15, obj.y + 15, 4, 0, Math.PI * 2);
        ctx.fill();
      });

      ctx.shadowBlur = 0;

      if (gameState === "playing") {
        update();
        requestRef.current = requestAnimationFrame(draw);
      }
    };

    if (gameState === "playing") {
      requestRef.current = requestAnimationFrame(draw);
    }

    return () => cancelAnimationFrame(requestRef.current);
  }, [gameState, update, targetSkill]);

  const onMouseMove = (e) => {
    if (isTouchDevice) return;
    const rect = containerRef.current.getBoundingClientRect();
    mouseX.current = (e.clientX - rect.left) * window.devicePixelRatio;
  };

  const onTouchMove = (e) => {
    const rect = containerRef.current.getBoundingClientRect();
    const touch = e.touches[0];
    mouseX.current = (touch.clientX - rect.left) * window.devicePixelRatio;
  };

  useEffect(() => {
    if (gameState === "gameOver" && score > highScore) {
      setHighScore(score);
      localStorage.setItem("meraki_talent_high_score", score.toString());
    }
  }, [gameState, score, highScore]);

  return (
    <section className="py-24 relative overflow-hidden bg-[#050505]">
      <div className="container-xl px-4 md:px-6">
        <div className="flex flex-col mb-12">
          <div className="text-center md:text-left mb-8">
            <Kicker index="CAREER-01" label="Interactive Talent Hunt" />
          </div>
          <div className="text-center">
            <h2 className="mt-4 font-display text-4xl md:text-5xl text-[var(--cream)]">
              The <span className="italic gold-text">Collective</span> Matcher
            </h2>
            <p className="mt-4 text-[var(--cream-muted)] text-sm uppercase tracking-[0.2em] max-w-lg mx-auto">
              We're looking for specialized talent. Match the skill requirements to join the team.
            </p>
            <button 
              onClick={() => setShowInstructions(true)}
              className="mt-6 flex items-center gap-2 text-[var(--gold)]/60 hover:text-[var(--gold)] transition-colors mx-auto text-[10px] font-impact tracking-widest uppercase"
            >
              <HelpCircle size={14} />
              How to Play
            </button>
          </div>
        </div>

        <div 
          ref={containerRef}
          className="relative max-w-4xl mx-auto aspect-[3/4] md:aspect-video rounded-[40px] overflow-hidden border border-[var(--rule)] bg-[#050505] shadow-[0_0_100px_rgba(0,0,0,0.5)] group"
          data-lenis-prevent
          onMouseMove={onMouseMove}
          onTouchMove={(e) => { e.stopPropagation(); onTouchMove(e); }}
          onWheel={(e) => e.stopPropagation()}
        >
          <canvas
            ref={canvasRef}
            className="w-full h-full cursor-none"
          />

          {/* UI Overlay */}
          <div className="absolute inset-0 pointer-events-none p-6 flex flex-col justify-between">
            <div className="flex justify-between items-start">
              <div className="space-y-1">
                <div className="text-[10px] tracking-widest text-[var(--muted)] uppercase">Current Requirement</div>
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full flex items-center justify-center bg-white/5 border border-white/10" style={{ borderColor: targetSkill.color }}>
                    <targetSkill.icon size={20} style={{ color: targetSkill.color }} />
                  </div>
                  <div className="font-display text-2xl text-[var(--cream)]">{targetSkill.label} Specialist</div>
                </div>
              </div>
              <div className="text-right">
                <div className="text-[10px] tracking-widest text-[var(--muted)] uppercase">Team Sync</div>
                <div className="flex gap-2 mt-1 justify-end">
                  {[...Array(3)].map((_, i) => (
                    <Heart 
                      key={i} 
                      size={18} 
                      className={i < health ? "text-red-500 fill-red-500" : "text-white/10"} 
                    />
                  ))}
                </div>
              </div>
            </div>

            <div className="flex justify-between items-end">
              <div className="font-impact text-4xl text-[var(--gold)]">
                {score}<span className="text-sm ml-1 opacity-50 text-[var(--cream)]">XP</span>
              </div>
              <div className="text-right">
                <div className="text-[10px] tracking-widest text-[var(--muted)] uppercase">High Sync</div>
                <div className="font-display text-xl text-[var(--cream)]">{highScore} XP</div>
              </div>
            </div>
          </div>

          {/* Screens */}
          <AnimatePresence>
            {gameState === "start" && (
              <motion.div 
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="absolute inset-0 z-20 flex flex-col items-center justify-center p-12 text-center"
              >
                {/* Game Thumbnail Background */}
                <div 
                  className="absolute inset-0 opacity-40 grayscale group-hover:grayscale-0 transition-all duration-1000"
                  style={{ 
                    backgroundImage: "url(/images/talent_matcher_thumb.png)",
                    backgroundSize: "cover",
                    backgroundPosition: "center"
                  }}
                />
                <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" />

                <div className="relative z-10 flex flex-col items-center">
                  <div className="mb-8 w-24 h-24 rounded-full border border-[var(--gold)] flex items-center justify-center relative">
                    <Target size={40} className="text-[var(--gold)]" />
                    <motion.div 
                      animate={{ scale: [1, 1.5], opacity: [0.5, 0] }}
                      transition={{ duration: 2, repeat: Infinity }}
                      className="absolute inset-0 rounded-full border border-[var(--gold)]"
                    />
                  </div>
                  <h3 className="font-display text-3xl md:text-4xl text-[var(--cream)] mb-4 italic text-center leading-tight">Start the Selection</h3>
                  <p className="text-[var(--cream-muted)] text-[8px] md:text-[10px] tracking-widest uppercase mb-8 md:mb-10 text-center leading-relaxed max-w-[85%] md:max-w-none">
                    {isTouchDevice ? "Drag to move scanner" : "Move mouse to scan"}. Catch the required skill gems. Missing gems or catching wrong ones reduces Team Sync.
                  </p>
                  <button 
                    onClick={startLevel}
                    className="btn-primary group"
                  >
                    <Play size={18} /> START MISSION
                  </button>
                </div>
              </motion.div>
            )}

            {gameState === "gameOver" && (
              <motion.div 
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="absolute inset-0 bg-black/90 backdrop-blur-xl z-20 flex flex-col items-center justify-center p-12 text-center"
              >
                <div className="mb-6 text-[var(--gold)]">
                  <Zap size={48} />
                </div>
                <h3 className="font-display text-4xl text-[var(--cream)] mb-2">Sync Lost</h3>
                <p className="text-[var(--cream-muted)] mb-8 text-sm uppercase tracking-widest">
                  Collective Intelligence score: {score} XP
                </p>
                <div className="flex gap-4">
                  <button onClick={startLevel} className="btn-primary group">
                    <RefreshCcw size={18} /> RETRY MISSION
                  </button>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>

      <GameInstructionsModal 
        isOpen={showInstructions}
        onClose={() => setShowInstructions(false)}
        title="Collective Matcher"
        instructions={[
          { title: "Identify the Target", desc: "Check the 'Current Requirement' HUD to see which skill specialist we are currently looking for (Film, Tech, Design, or Strategy)." },
          { title: "Catch the Gems", desc: "Move your mouse left and right to align your scanner with the falling skill gems. Catch the gems that match the current requirement." },
          { title: "Avoid Mismatches", desc: "Catching the wrong gem or missing a required gem will reduce your 'Team Sync' (health). You have 3 lives before the mission fails." },
          { title: "Build the Team", desc: "Successfully matching gems increases your XP. The requirements will change as you gain more XP, so stay alert!" }
        ]}
      />
    </section>
  );
};

const Kicker = ({ index, label }) => (
  <div className="flex items-center justify-center md:justify-start gap-4">
    <span className="font-impact text-[11px] tracking-[0.4em] text-[var(--gold)]">{index}</span>
    <span className="w-16 h-px bg-[var(--rule)]" />
    <span className="font-impact text-[11px] tracking-[0.4em] text-[var(--cream-muted)] uppercase">{label}</span>
  </div>
);

export default TalentMatcherGame;
